import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/contexts/AuthContext";
import SharedAccessNotification from "@/components/team/SharedAccessNotification";
import { getAllWorkspaces } from "@/lib/api/workspaces";
import { generateSlugId } from "@/lib/routing/slug-id";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BrandIcon, BRAND_ICONS } from "@/components/ui/brand-icons";
import { InlineNotice } from "@/components/shared/layout";
import HumblyticsLogo from "@/assets/humblytics-web-app-logo.svg";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight01Icon, PlusSignIcon } from "@hugeicons/core-free-icons";

type WorkspaceRow = {
  workspaceId: string;
  name?: string;
  properties?: { domain?: string; name?: string }[];
  connectors?: { type: string; status?: string }[];
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Paper "Workspaces · List": brand bar on the canvas, one raised table card. */
export default function SitesDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Workspaces - Humblytics";
  }, []);

  const load = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setWorkspaces(await getAllWorkspaces(token));
    } catch (err) {
      console.error("Error loading workspaces:", err);
      setError("Couldn't load your workspaces. Try again in a moment.");
      setWorkspaces([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const initials = (user?.name || user?.email || "U")
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const createButton = (
    <Button onClick={() => navigate("/workspace/new")} className="h-[34px] gap-1.5 rounded-lg px-3 text-[13px]">
      <HugeiconsIcon icon={PlusSignIcon} size={13} />
      Create workspace
    </Button>
  );

  return (
    <div className="min-h-svh bg-canvas text-foreground">
      <header className="flex h-[60px] items-center justify-between px-4 sm:px-7">
        <Link to="/dashboard" className="flex items-center gap-2.5">
          <img src={HumblyticsLogo} alt="" className="size-[26px]" />
          <span className="text-sm font-semibold leading-[18px]">Humblytics</span>
        </Link>
        <Link
          to="/user-settings"
          className="flex h-[34px] items-center gap-2 rounded-full bg-background py-1 pl-1 pr-3 shadow-[0_0_0_1px_var(--input)] transition-colors hover:bg-muted/60"
        >
          <span className="flex size-[26px] items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground/70">
            {initials}
          </span>
          <span className="max-w-[200px] truncate text-xs text-foreground/70">{user?.email}</span>
        </Link>
      </header>

      <main className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-4 pb-16 pt-8 sm:pt-11">
        <SharedAccessNotification />
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] sm:text-[28px] sm:leading-[34px]">Workspaces</h1>
            <p className="text-sm leading-[18px] text-foreground/60">Pick a workspace to manage its sites, data sources and reports.</p>
          </div>
          {createButton}
        </div>

        <nav aria-label="Workspaces sections" className="flex gap-[22px] border-b border-border">
          <span aria-current="page" className="flex h-11 items-center text-[13px] font-medium text-foreground shadow-[inset_0_-2px_0_var(--foreground)]">
            Workspaces
          </span>
          <Link to="/user-settings" className="flex h-11 items-center text-[13px] font-medium text-foreground/60 transition-colors hover:text-foreground">
            Account settings
          </Link>
        </nav>

        {error ? (
          <div className="surface-raised flex flex-col items-start gap-3 p-5">
            <InlineNotice variant="error" message="Workspaces didn't load" description={error} />
            <Button variant="outline" onClick={() => void load()} className="h-[34px] rounded-lg border-input bg-background px-3 text-xs shadow-none">
              Try again
            </Button>
          </div>
        ) : !isLoading && workspaces.length === 0 ? (
          <div className="surface-raised flex flex-col items-center gap-2 px-6 py-16 text-center">
            <h2 className="text-base font-semibold leading-5">No workspaces yet</h2>
            <p className="max-w-sm text-[13px] leading-5 text-foreground/60">
              A workspace groups your sites, connectors and team. Create one to add your first site.
            </p>
            <div className="mt-3">{createButton}</div>
          </div>
        ) : (
          <section className="surface-raised overflow-clip">
            <div className="hidden h-9 items-center gap-4 border-b border-border px-5 text-xs leading-4 text-foreground/60 sm:flex">
              <span className="w-9 shrink-0" />
              <span className="flex-1">Workspace</span>
              <span className="w-40 shrink-0">Connectors</span>
              <span className="w-24 shrink-0 text-right">Sites</span>
              <span className="w-4 shrink-0" />
            </div>
            <ul className="flex flex-col [&>li+li]:border-t [&>li+li]:border-border">
              {isLoading
                ? [0, 1, 2].map((i) => (
                    <li key={i} className="flex h-[68px] items-center gap-4 px-5">
                      <Skeleton className="size-9 rounded-lg" />
                      <div className="flex flex-1 flex-col gap-1.5">
                        <Skeleton className="h-3.5 w-36" />
                        <Skeleton className="h-3 w-56" />
                      </div>
                    </li>
                  ))
                : workspaces.map((ws) => {
                    const slugId = generateSlugId(ws.name || "workspace", ws.workspaceId);
                    const sites = ws.properties?.length ?? 0;
                    const domains = (ws.properties ?? []).map((p) => p.domain || p.name).filter(Boolean).join(", ");
                    const connected = Array.from(
                      new Set((ws.connectors ?? []).filter((c) => c.status === "connected").map((c) => c.type)),
                    ).filter((type): type is keyof typeof BRAND_ICONS => type in BRAND_ICONS && type !== "humblytics");
                    return (
                      <li key={ws.workspaceId}>
                        <Link
                          to={`/workspace/${slugId}`}
                          className="flex min-h-[68px] items-center gap-4 px-5 py-3 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
                        >
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-sm font-semibold text-background">
                            {(ws.name || "W").charAt(0).toUpperCase()}
                          </span>
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate text-sm font-medium leading-[18px]">{ws.name || "Untitled workspace"}</span>
                            <span className="truncate text-xs leading-4 text-foreground/60">{domains || "No sites yet"}</span>
                          </span>
                          <span className="hidden w-40 shrink-0 items-center gap-1.5 sm:flex">
                            {connected.length > 0 ? (
                              connected.map((type) => (
                                <span key={type} className="flex size-7 items-center justify-center rounded-md bg-card shadow-[0_0_0_1px_var(--input)]" title={type}>
                                  <BrandIcon brand={type} className="size-4 object-contain" />
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-foreground/45">None yet</span>
                            )}
                          </span>
                          <span className="flex w-24 shrink-0 justify-end">
                            <span className="inline-flex h-5 items-center rounded-md bg-muted px-1.5 text-xs font-medium leading-4 tabular-nums">
                              {plural(sites, "site", "sites")}
                            </span>
                          </span>
                          <HugeiconsIcon icon={ArrowRight01Icon} size={15} className="w-4 shrink-0 text-foreground/35" />
                        </Link>
                      </li>
                    );
                  })}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
